"use client";

import { useMemo, useState } from "react";
import Link from "next/link";
import { motion, useReducedMotion } from "@/lib/motion";
import { ArrowLeft } from "@/components/icons";
import { EyebrowBadge } from "@/components/ui/eyebrow-badge";
import { HeroGlow } from "@/components/ui";
import { cn } from "@/lib/utils";
import { CHANGELOG_ENTRIES, PRODUCT_CONFIG } from "./data";
import { TimelineEntry } from "./parts";
import type { Product } from "./types";

const FILTERS: { key: Product; label: string }[] = [
  { key: "mobile", label: PRODUCT_CONFIG.mobile.label },
  { key: "residential", label: PRODUCT_CONFIG.residential.label },
  { key: "builder", label: PRODUCT_CONFIG.builder.label },
];

export default function ChangelogSection() {
  const prefersReducedMotion = useReducedMotion();
  const [filter, setFilter] = useState<Product>("mobile");

  const entries = useMemo(
    () => CHANGELOG_ENTRIES.filter((e) => e.products.includes(filter)),
    [filter]
  );

  const latest = entries[0];
  const latestLabel = latest
    ? latest.isSpecial || latest.isInitial
      ? latest.version
      : `v${latest.version}`
    : null;

  return (
    <main id="main-content" tabIndex={-1} className="relative overflow-y-clip pt-32 pb-32">
      <HeroGlow align="left" />
      <div className="relative mx-auto max-w-4xl px-4 sm:px-6 lg:px-8">
        <motion.div
          initial={prefersReducedMotion ? false : { opacity: 0, y: 14 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.45, ease: [0.22, 1, 0.36, 1] }}
          className="mb-12"
        >
          <Link
            href="/"
            className="text-muted-foreground hover:text-foreground group mb-8 inline-flex items-center gap-1.5 text-sm transition-colors"
          >
            <ArrowLeft size={14} className="transition-transform group-hover:-translate-x-0.5" />
            Back to home
          </Link>

          <div className="flex flex-wrap items-start justify-between gap-6">
            <div>
              <EyebrowBadge>Product Updates</EyebrowBadge>
              <h1 className="text-foreground mb-3 text-4xl font-bold tracking-tight sm:text-5xl">
                Changelog
              </h1>
              <p className="text-muted-foreground max-w-sm text-sm leading-relaxed">
                New features, improvements, and fixes — directly from the Proxidize team.
              </p>
            </div>

            {latestLabel && (
              <div className="text-muted-foreground mt-1 flex items-center gap-2 text-xs">
                <span className="relative flex h-2 w-2">
                  <span
                    className="absolute inline-flex h-full w-full animate-ping rounded-full opacity-60"
                    style={{ backgroundColor: "#5b8cff" }}
                  />
                  <span
                    className="relative inline-flex h-2 w-2 rounded-full"
                    style={{ backgroundColor: "#5b8cff" }}
                  />
                </span>
                Latest: {latestLabel}
              </div>
            )}
          </div>

          <div
            className="border-border mt-8 flex flex-wrap items-center gap-2 border-t pt-6"
            role="tablist"
            aria-label="Filter changelog by product"
          >
            {FILTERS.map(({ key, label }) => {
              const active = filter === key;
              return (
                <button
                  key={key}
                  type="button"
                  role="tab"
                  aria-selected={active}
                  onClick={() => setFilter(key)}
                  className={cn(
                    "inline-flex items-center rounded-full border px-3 py-1.5 text-xs font-medium transition-colors",
                    active
                      ? "bg-foreground text-background border-foreground"
                      : "border-border text-muted-foreground hover:text-foreground hover:border-foreground/30"
                  )}
                >
                  {label}
                </button>
              );
            })}
          </div>
        </motion.div>

        <div className="relative">
          {entries.length === 0 ? (
            <div className="border-border bg-card text-muted-foreground rounded-xl border p-8 text-center text-sm">
              No updates yet for this product.
            </div>
          ) : (
            entries.map((entry, index) => (
              <TimelineEntry key={entry.version + entry.date} entry={entry} index={index} />
            ))
          )}
        </div>
      </div>
    </main>
  );
}
